import { useCallback, useEffect, useRef, useState } from 'react'
import { CrmApi, CrmConnection } from './api'
import { errorMessage } from 'pages/workspace/asset/lead-forms/api'

/** Only the popup we opened and its single-use state can complete this operation. */
export const acceptsHubSpotCallback = (
	event: MessageEvent,
	popup: Window | null,
	state: string
) =>
	!!popup &&
	!!state &&
	event.origin === window.location.origin &&
	event.source === popup &&
	event.data?.type === 'cg-hubspot-callback' &&
	event.data?.state === state

export const useHubSpotConnect = (
	api: CrmApi | undefined,
	onConnected: (connection: CrmConnection) => void
) => {
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState('')
	const callback = useRef(onConnected)
	callback.current = onConnected
	const cleanup = useRef<() => void>(() => undefined)
	useEffect(() => () => cleanup.current(), [api])
	const connect = useCallback(
		async (connectionId?: string) => {
			if (!api) return
			cleanup.current()
			setError('')
			const popup = window.open(
				'about:blank',
				'cg-hubspot-oauth',
				'width=600,height=720,popup=yes'
			)
			if (!popup) {
				setError('Allow popups to connect your HubSpot account.')
				return
			}
			setBusy(true)
			let finished = false
			let completing = false
			let state = ''
			let poll: number | undefined
			const dispose = () => {
				finished = true
				window.clearInterval(poll)
				window.removeEventListener('message', receive)
				popup.close()
			}
			const receive = async (event: MessageEvent) => {
				if (
					finished ||
					completing ||
					!acceptsHubSpotCallback(event, popup, state)
				)
					return
				completing = true
				try {
					if (event.data.error || !event.data.code)
						throw new Error(
							'HubSpot authorization was cancelled or denied. Please try again.'
						)
					const connection = await api.complete(event.data.code, state)
					if (!finished) callback.current(connection)
				} catch (e) {
					if (!finished) setError(errorMessage(e))
				} finally {
					if (!finished) setBusy(false)
					dispose()
				}
			}
			cleanup.current = () => {
				dispose()
				setBusy(false)
			}
			window.addEventListener('message', receive)
			try {
				const result = await api.start(connectionId)
				if (finished) return
				state = result.state
				popup.location.href = result.url
				poll = window.setInterval(() => {
					if (popup.closed && !completing) {
						dispose()
						setBusy(false)
						setError('HubSpot connection was cancelled. You can try again.')
					}
				}, 400)
			} catch (e) {
				if (!finished) {
					setError(errorMessage(e))
					setBusy(false)
				}
				dispose()
			}
		},
		[api]
	)
	return { connect, busy, error }
}
